/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { PureComponent, createContext } from 'react';
import { View, StyleSheet } from 'react-native';
import {
  AutoSizer,
  Grid,
  GridCellRenderer,
  ScrollSync,
} from 'react-virtualized/dist/commonjs';

import { themeColors } from '../uiold/Constants';

import {
  KEY_UP_ARROW,
  KEY_DOWN_ARROW,
  KEY_LEFT_ARROW,
  KEY_RIGHT_ARROW,
  KEY_SPACE,
} from '../../support/KeyCodes';
import Cell, { OuterCell } from './Cell';
import PlacementActions from '../../support/PlacementActions';
import formatDate from '../../core/support/formatDate';

import { AttendanceByMarket } from '../../types/shared/Attendance';
import { PlacementByVendor } from '../../types/shared/Placement';
import { Market } from '../../types/shared/Market';
import { Tag } from '../../types/shared/Tag';
import {
  VendorList,
  VendorSummaryWithMarkets as Vendor,
} from '../../types/shared/Vendor';
import { PositionMap } from '../../types/shared/PositionMap';
import { AssignVendorModal } from './AssignVendorModal';
import { splitCellID } from './helpers/splitCellID';
import { DateStatus } from '../../runtypes/VendorRuntype';
import { confirm } from '../App/AppDialog';

type CellID = string; // Format is colNum:rowNum such as `12:5`
type CellData = {
  index: number;
  date: string;
  vendor: Vendor;
};
type Placement = Map<CellID, CellData>;

/** Options for placing a vendor to a single cell */
type Options = {
  onlyIfStatus?: Array<DateStatus>;
};

type Props = {
  attendance: AttendanceByMarket | null | undefined;
  tags: Array<Tag> | null | undefined;
  market: Market;
  placementByVendor: PlacementByVendor;
  positionMap: PositionMap | null | undefined;
  vendors: VendorList;
  canEdit?: boolean | null | undefined;
};

type State = {
  cursor: CellID;
  placement: Placement;
  selected: Set<CellID>;
  isModalAssignVisible: boolean;
};

const CELL_WIDTH = 151;
const CELL_HEIGHT = 51;
const EMPTY_SELECTED: Set<CellID> = new Set();

type Context = { selected: Set<CellID> } | void;

export const SelectedContext = createContext<Context>(undefined as any);

class AdvancedView extends PureComponent<Props, State> {
  state: State = {
    cursor: '0:0',
    isModalAssignVisible: false,
    selected: EMPTY_SELECTED,
    placement: getComputedPlacement(this.props),
  };

  _timeOut: TimeoutID;

  componentDidMount() {
    document.addEventListener('keydown', this._onKeyDown);
  }

  componentDidUpdate(prevProps: Props) {
    let { placementByVendor } = this.props;
    if (prevProps.placementByVendor !== placementByVendor) {
      this.setState({
        placement: getComputedPlacement(this.props),
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this._onKeyDown);
    clearTimeout(this._timeOut);
  }

  render() {
    let { market } = this.props;
    let numSpots = market.num_spots;
    let datesLength = market.dates.length;

    return (
      <View style={styles.root}>
        <ScrollSync>
          {({ onScroll, scrollLeft, scrollTop }) => {
            return (
              <AutoSizer>
                {({ height, width }) => (
                  <View style={styles.scrollSyncRoot}>
                    <View style={styles.topLeftCell}>
                      {/* Corner Cell */}
                      <Grid
                        cellRenderer={this._renderLeftHeaderCell}
                        rowHeight={CELL_HEIGHT}
                        columnWidth={CELL_WIDTH}
                        width={CELL_WIDTH}
                        height={CELL_HEIGHT}
                        rowCount={1}
                        columnCount={1}
                      />
                      {/* Row Header */}
                      <Grid
                        style={
                          StyleSheet.flatten(styles.leftHeaderColumn) as any
                        }
                        columnCount={1}
                        width={CELL_WIDTH}
                        overscanRowCount={5}
                        rowCount={numSpots}
                        scrollTop={scrollTop}
                        rowHeight={CELL_HEIGHT}
                        columnWidth={CELL_WIDTH}
                        overscanColumnCount={0}
                        height={height - CELL_HEIGHT}
                        cellRenderer={this._renderLeftSideCell}
                      />
                    </View>

                    <View
                      style={{
                        left: CELL_WIDTH,
                        top: 0,
                        flex: 1,
                      }}
                    >
                      {/* Column Header */}
                      <Grid
                        rowCount={1}
                        style={
                          StyleSheet.flatten([
                            styles.headerRow,
                            {
                              width: width - CELL_WIDTH,
                            },
                          ]) as any
                        }
                        width={width - CELL_WIDTH}
                        height={CELL_HEIGHT}
                        columnCount={datesLength}
                        overscanColumnCount={5}
                        columnWidth={CELL_WIDTH}
                        rowHeight={CELL_HEIGHT}
                        scrollLeft={scrollLeft}
                        cellRenderer={this._renderHeaderCell}
                      />
                      {/* Body */}
                      <View style={styles.flexOne}>
                        <SelectedContext.Provider
                          value={{
                            selected: this.state.selected,
                          }}
                        >
                          <Grid
                            {...this.state}
                            cellRenderer={this._renderVirtualCell}
                            width={width - CELL_WIDTH}
                            height={height - CELL_HEIGHT}
                            rowHeight={CELL_HEIGHT}
                            columnWidth={CELL_WIDTH}
                            rowCount={numSpots}
                            columnCount={datesLength}
                            onScroll={onScroll}
                            overscanColumnCount={5}
                            overscanRowCount={5}
                          />
                        </SelectedContext.Provider>
                      </View>
                    </View>
                  </View>
                )}
              </AutoSizer>
            );
          }}
        </ScrollSync>
        {this._renderAssignVendorModal()}
      </View>
    );
  }

  _renderAssignVendorModal = () => {
    const { tags, market, placementByVendor, vendors } = this.props;
    const { cursor, selected, placement, isModalAssignVisible } = this.state;
    return (
      <AssignVendorModal
        isOpen={isModalAssignVisible}
        onClose={this._onModalAssignClose}
        selectedCells={selected.size ? Array.from(selected) : [cursor]}
        onVendorSelect={(vendor) => {
          this._onModalAssignClose();
          void this._assignVendor(vendor);
        }}
        placement={placement}
        tags={tags ?? []}
        market={market}
        placementByVendor={placementByVendor}
        vendors={vendors.getList()}
      />
    );
  };

  _renderLeftHeaderCell: GridCellRenderer = ({ key, style }) => {
    return (
      <OuterCell
        key={key}
        colIndex={0}
        rowIndex={0}
        style={style as any}
        text="Stall"
      />
    );
  };

  _renderLeftSideCell: GridCellRenderer = ({ key, rowIndex, style }) => {
    let { positionMap } = this.props;

    if (positionMap && Object.keys(positionMap).length) {
      return (
        <OuterCell
          key={key}
          eastCursor
          colIndex={0}
          style={style as any}
          rowIndex={rowIndex}
          text={positionMap[rowIndex]}
          onPress={this._onRowFocus}
        />
      );
    } else {
      return (
        <OuterCell
          key={key}
          style={style as any}
          eastCursor
          colIndex={0}
          rowIndex={rowIndex}
          text={String(rowIndex + 1)}
          onPress={this._onRowFocus}
        />
      );
    }
  };

  _renderHeaderCell: GridCellRenderer = ({ columnIndex, key, style }) => {
    let { market } = this.props;
    let { dates } = market;

    return (
      <OuterCell
        key={key}
        style={style as any}
        colIndex={columnIndex}
        rowIndex={0}
        text={formatDate(dates[columnIndex] ?? '', {
          fromUTC: true,
          includeDayOfWeek: true,
        })}
        onPress={this._onColFocus}
      />
    );
  };

  _renderVirtualCell: GridCellRenderer = ({
    columnIndex,
    key,
    rowIndex,
    style,
  }) => {
    let { dates, num_spots: numSpots } = this.props.market;
    let { canEdit } = this.props;
    let cellID = getCellID(columnIndex, rowIndex);
    let isSelected = this.state.selected.has(cellID);
    let hasCursor = this.state.cursor === cellID;
    let placementData = this.state.placement.get(cellID);
    return (
      <Cell
        {...this.state.selected}
        key={key}
        style={style as any}
        onRemove={() => {
          this._removeVendor();
        }}
        openAssignModal={this._onModalAssignOpen}
        colIndex={columnIndex}
        rowIndex={rowIndex}
        hasCursor={hasCursor}
        tags={this.props.tags}
        isSelected={isSelected}
        onFocus={this._onCellFocus}
        singleColumn={dates.length === 1}
        firstSpot={rowIndex === 0}
        lastSpot={rowIndex + 1 === numSpots}
        lastDate={columnIndex + 1 === dates.length}
        vendor={placementData && placementData.vendor}
        canEdit={canEdit}
      />
    );
  };

  _onKeyDown = (event: KeyboardEvent) => {
    let { cursor, isModalAssignVisible } = this.state;
    if (isModalAssignVisible) {
      return;
    }
    let isHandled = true;
    switch (event.keyCode) {
      case KEY_UP_ARROW:
        this._moveCursor(event, -1, 0);

        break;
      case KEY_DOWN_ARROW:
        this._moveCursor(event, 1, 0);

        break;
      case KEY_LEFT_ARROW:
        this._moveCursor(event, 0, -1);

        break;
      case KEY_RIGHT_ARROW:
        this._moveCursor(event, 0, 1);

        break;
      case KEY_SPACE:
        let [y, x] = splitCellID(cursor);

        this._onCellFocus(y, x, undefined, event);

        break;
      default:
        isHandled = false;
    }
    if (isHandled) {
      event.preventDefault();
    }
  };

  _moveCursor = (event: KeyboardEvent, deltaX: number, deltaY: number) => {
    let { dates, num_spots: numSpots } = this.props.market;
    let maxX = Math.max(numSpots - 1, 0);
    let maxY = Math.max(dates.length - 1, 0);

    let { cursor } = this.state;
    let [y, x] = splitCellID(cursor);
    y = clamp(y + deltaY, 0, maxY);
    x = clamp(x + deltaX, 0, maxX);

    if (event.shiftKey) {
      this._onCellFocus(y, x, undefined, event);
    } else {
      this.setState({
        cursor: getCellID(y, x),
      });
    }
  };

  _onColFocus = (
    colIndex: number,
    rowIndex: number,
    mouseEvent?: MouseEvent | undefined,
  ) => {
    let { selected } = this.state;
    let nextSelected = new Set(selected);

    for (let i = 0; i < this.props.market.num_spots; i++) {
      nextSelected.add(getCellID(colIndex, i));
    }

    let nextCell = getCellID(colIndex, 0);

    if (mouseEvent?.ctrlKey) {
      this.setState({
        selected: nextSelected,
      });
    } else {
      this.setState({
        cursor: nextCell,
        selected: nextSelected,
      });
    }
  };

  _onRowFocus = (
    colIndex: number,
    rowIndex: number,
    mouseEvent?: MouseEvent | undefined,
  ) => {
    let { selected } = this.state;
    let nextSelected = new Set(selected);
    for (let i = 0; i < this.props.market.dates.length; i++) {
      nextSelected.add(getCellID(i, rowIndex));
    }
    let nextCell = getCellID(0, rowIndex);
    if (mouseEvent?.ctrlKey) {
      this.setState({
        selected: nextSelected,
      });
    } else {
      this.setState({
        cursor: nextCell,
        selected: nextSelected,
      });
    }
  };

  _onCellFocus = (
    colIndex: number,
    rowIndex: number,
    mouseEvent?: MouseEvent | undefined,
    keyboardEvent?: FocusEvent | KeyboardEvent | undefined,
  ) => {
    let { selected, cursor, isModalAssignVisible } = this.state;

    let prevCell = cursor;
    let nextCell = getCellID(colIndex, rowIndex);
    let nextSelected = new Set(selected);

    if (isModalAssignVisible) {
      return;
    }
    if (mouseEvent) {
      if (mouseEvent.shiftKey) {
        let [y, x] = splitCellID(prevCell);
        let deltaY = colIndex - y;
        let deltaX = rowIndex - x;
        let multiplierY = deltaY < 0 ? -1 : 1;
        let multiplierX = deltaX < 0 ? -1 : 1;

        for (
          let columnIndex = 0;
          columnIndex < Math.abs(deltaY) + 1;
          columnIndex++
        ) {
          for (let rowIndex = 0; rowIndex < Math.abs(deltaX) + 1; rowIndex++) {
            nextSelected.add(
              getCellID(
                y + columnIndex * multiplierY,
                x + rowIndex * multiplierX,
              ),
            );
          }
        }
      } else if (mouseEvent.metaKey || mouseEvent.ctrlKey) {
        // If there is no selection yet, add the current cell to the selection.
        if (selected.size === 0) {
          nextSelected.add(prevCell);
        }
        // Toggle the cell that was pressed.
        nextSelected.has(nextCell)
          ? nextSelected.delete(nextCell)
          : nextSelected.add(nextCell);
      } else {
        nextSelected.clear();
        // nextSelected.add(nextCell);
      }
    } else if (keyboardEvent) {
      if (Object(keyboardEvent).keyCode === KEY_SPACE) {
        nextSelected.has(nextCell)
          ? nextSelected.delete(nextCell)
          : nextSelected.add(nextCell);
      } else {
        // This is expected to be a shift + arrow key.
        nextSelected.add(cursor);
        nextSelected.add(nextCell);
      }
    } else {
    }
    this.setState({
      cursor: nextCell,
      selected: nextSelected,
    });
  };

  _save = (
    vendor: Vendor,
    market: Market,
    dates: Array<string>,
    dateIndex: number,
    spot: number,
  ) => {
    let date = dates[dateIndex];
    PlacementActions.addVendorPlacement(market, date ?? '', spot, vendor);
  };

  _assignVendor = async (vendor: Vendor) => {
    const { cursor, selected } = this.state;
    if (selected.size > 1) {
      for (const cellId of selected) {
        await this._assignVendorToCell(vendor, cellId, {
          onlyIfStatus: ['APPROVED'],
        });
      }
      this.setState({
        selected: new Set(),
      });
    } else {
      await this._assignVendorToCell(vendor, cursor, {
        onlyIfStatus: ['APPROVED', 'WAITLISTED'],
      });
    }
  };

  _assignVendorToCell = async (
    vendor: Vendor,
    cellId: string,
    opts?: Options,
  ) => {
    const { market } = this.props;
    const { placement } = this.state;
    const [dateIndex, spotIndex] = splitCellID(cellId);
    const date = market.dates[dateIndex] ?? '';
    const vendorDateStatus = this._getDateStatus(vendor, date);
    const onlyIfStatus = opts?.onlyIfStatus;
    if (onlyIfStatus) {
      if (!vendorDateStatus || !onlyIfStatus.includes(vendorDateStatus)) {
        return;
      }
    }
    if (vendorDateStatus === 'WAITLISTED') {
      const proceed = await confirm(
        t('Place waitlisted vendor?'),
        t('This will mark this date as approved for this vendor.'),
      );
      if (!proceed) {
        return;
      }
    }
    if (placement.has(cellId)) {
      const placementData = placement.get(cellId);
      if (placementData?.vendor.id === vendor.id) {
        // Vendor already placed in this spot
        return;
      }
      this._removeSingle(cellId);
    }
    this._save(vendor, market, market.dates, dateIndex, spotIndex);
  };

  _getDateStatus = (vendor: Vendor, date: string) => {
    const { market } = this.props;
    const dateSelectionByMarket = vendor.dateSelectionByMarket ?? {};
    const dateSelection = dateSelectionByMarket[market.id] ?? {};
    return dateSelection[date];
  };

  _removeVendorByCellId = (cellIds: Set<string>) => {
    const { cursor, placement } = this.state;
    if (cellIds.size > 0) {
      cellIds.forEach((cell) => {
        if (placement.has(cell)) {
          this._removeSingle(cell);
        }
      });
    } else if (cursor && placement.has(cursor)) {
      this._removeSingle(cursor);
    }
  };

  _removeVendor = () => {
    let { selected } = this.state;
    this._removeVendorByCellId(selected);
    this.setState({
      selected: new Set(),
    });
  };

  _removeSingle = (cell: CellID) => {
    const { placement } = this.state;
    const { market } = this.props;

    const data = placement.get(cell);
    if (!data) {
      return null;
    }
    PlacementActions.removeVendorPlacement(
      market,
      data.date,
      data.index,
      data.vendor,
    );
    return data.vendor.id;
  };

  _onModalAssignOpen = () => {
    this.setState({
      isModalAssignVisible: true,
    });
  };

  _onModalAssignClose = () => {
    this.setState({ isModalAssignVisible: false });
  };
}

// TODO: Memoize this function (if necessary)
function getComputedPlacement(props: Props): Placement {
  let { placementByVendor, vendors, market } = props;
  let { id: _marketID, dates, num_spots: numSpots } = market;
  let placement = new Map();
  type DateAndIndex = string; // 2018-10-22:9
  let getDateAndIndex = (date: string, rowIndex: number) =>
    `${date}:${rowIndex}`;
  let placementBySpot: {
    [key in DateAndIndex]?: Vendor;
  } = {};
  for (let vendorID of Object.keys(placementByVendor)) {
    let dateToPosition = placementByVendor[vendorID]?.[market.id];
    if (!dateToPosition) {
      continue;
    }
    for (let date of Object.keys(dateToPosition)) {
      let vendor = vendors.getByID(vendorID);
      if (
        !vendor
        // || (vendor.status !== 'Active' && vendor.status !== 'Approved') ||
        // !vendor.markets ||
        // !vendor.markets[marketID] ||
        // vendor.markets[marketID]?.indexOf(date) === -1
      ) {
        continue;
      }
      for (let spotNum of dateToPosition[date] || []) {
        let key = getDateAndIndex(date, spotNum);
        placementBySpot[key] = vendor;
      }
    }
  }

  for (let colIndex = 0; colIndex < dates.length; colIndex++) {
    let date = dates[colIndex];
    for (let rowIndex = 0; rowIndex < numSpots; rowIndex++) {
      let key = getDateAndIndex(date ?? '', rowIndex);
      let vendor = placementBySpot[key];
      if (vendor) {
        let cellID = getCellID(colIndex, rowIndex);
        placement.set(cellID, {
          vendor,
          index: rowIndex,
          date,
        });
      }
    }
  }
  return placement;
}

function clamp(x: number, lower: number, upper: number) {
  return Math.min(Math.max(x, lower), upper);
}

function getCellID(colIndex: number, rowIndex: number) {
  return `${colIndex}:${rowIndex}`;
}

let styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: themeColors.borderGrey,
  },
  flexOne: {
    flex: 1,
  },
  topLeftCell: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
  leftHeaderColumn: {
    position: 'absolute',
    overflow: 'hidden',
    top: CELL_HEIGHT,
    left: 0,
    overflowY: 'hidden',
  } as any,
  scrollSyncRoot: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: themeColors.borderGrey,
  },
  headerRow: {
    flexDirection: 'row',
    overflowX: 'hidden',
  } as any,
});

export default AdvancedView;
